import type { LevelSim } from "@highmarch/domain";
import { endlessBossWave } from "@highmarch/domain";
import { StageSegment, stageSegments } from "./kit";

/**
 * How a run's waves read on the HUD and the end screen (docs/MODES.md): a level's "Wave 12 of 24", an Endless run's
 * "Wave 12" (no end: `stages` is only its safety cap), an Iron run's one long wave. Presentation only.
 */

type WaveSim = Pick<LevelSim, "stage" | "stages" | "stagesCleared" | "isEndless" | "isIron" | "balance">;

/** The HUD wave plate's title. */
export function waveTitle(sim: WaveSim): string {
  if (sim.isIron) return "The Iron wave";
  return sim.isEndless ? `Wave ${sim.stage}` : `Wave ${sim.stage} of ${sim.stages}`;
}

/** The stage pill's short text. */
export function stageShort(sim: WaveSim): string {
  if (sim.stage === 0) return "READY";
  if (sim.isIron) return "IRON";
  return sim.isEndless ? `W${sim.stage}` : `${sim.stage}/${sim.stages}`;
}

/** "Wave 3 of 24" (the boss announcement's line), "Wave 15" on Endless, "The Iron wave" on Iron. */
export function stageLine(sim: WaveSim): string {
  if (sim.isIron) return "The Iron wave";
  return sim.isEndless ? `Wave ${sim.stage}` : `Wave ${sim.stage} of ${sim.stages}`;
}

/** Whether `stage` is the run's last (the banner's "FINAL"): never on Endless. */
export function isFinalStage(sim: WaveSim, stage: number): boolean { return !sim.isEndless && !sim.isIron && stage === sim.stages; }

/**
 * The stage bar's cells: the level's every stage; on Endless the five-wave window the run is in, its boss wave marked
 * (every `bossEvery`), so the bar never grows with the run.
 */
export function stageBar(sim: WaveSim): StageSegment[] {
  if (!sim.isEndless) return stageSegments(sim.stage, sim.stagesCleared, sim.stages);
  const every = Math.max(1, sim.balance.modes.endless.bossEvery);
  const base = Math.floor(Math.max(0, sim.stage - 1) / every) * every;
  const bosses = Array.from({ length: every }, (_, i) => i + 1).filter((i) => endlessBossWave(sim.balance, base + i));
  return stageSegments(Math.max(0, sim.stage - base), Math.max(0, sim.stagesCleared - base), every, bosses);
}

/** MP-21: the HUD title of an event mode's run ("Boss Week · Tier 3", "Race Week · The Bend", "Odyssey · Map 2/3"). */
export function eventRunTitle(sim: Pick<LevelSim, "modifiers" | "level" | "balance">): string {
  const e = sim.modifiers.event;
  if (!e) return sim.level.name;
  if (e.mode === "boss") return `Boss Week · ${e.step === sim.balance.events.modes?.boss.tiers.length ? "Elite" : `Tier ${e.step}`}`;
  if (e.mode === "race") return `Race Week · ${sim.level.name}`;
  return `Odyssey · Map ${e.step}/${sim.balance.events.modes?.odyssey.maps.length ?? 3}`;
}
